<template>
  <div>
    <div class="header">
      <h2>Usage calculations</h2>
      <div class="datePickers">
        <div class="monthPicker">
          <month-picker
            v-model="chartDates.from_date"
            :min="minFromDate"
            :max="maxFromDate"
            label="From date"
          />
        </div>
        <div class="monthPicker">
          <month-picker
            v-model="chartDates.to_date"
            :min="minToDate"
            :max="maxToDate"
            label="To date"
          />
        </div>
        <a :href="exportUrl" download>
          <v-btn color="primary" primary>Download details</v-btn>
        </a>
      </div>
    </div>
    <v-autocomplete
      v-model="company_ids"
      multiple
      :items="companies"
      label="Choose company"
      class="autocompleteWidth mb-2"
      item-value="id"
      item-text="name"
    />
    <div>
      <canvas :id="chartId" height="500px" />
    </div>
  </div>
</template>

<script>
import api from '@/api'
import Chart from 'chart.js/auto'
import { useUserStore } from '@/stores/UserStore'
import MonthPicker from '@/components/MonthPicker.vue'

function* colorGenerator() {
  while (true) {
    yield 'rgb(236,122,148)'
    yield 'rgb(171,241,139)'
    yield 'rgb(130,165,231)'
    yield 'rgb(241,229,131)'
    yield 'rgb(192,135,225)'
    yield 'rgb(126,232,200)'
  }
}

export default {
  name: 'CalculationUsage',
  components: { MonthPicker },

  setup() {
    const userStore = useUserStore()
    return {
      userStore,
    }
  },

  data() {
    return {
      chartId: 'calculationChart',
      chart: null,
      chartDates: {
        from_date: this.getDefaultStartDate(),
        to_date: this.getDefaultEndDate(),
      },
      chartData: {
        labels: [],
        datasets: [],
      },
      allEnvironments: [],
      apiData: [],
      companies: [],
      company_ids: [],
    }
  },

  computed: {
    exportUrl() {
      const url = new URL(
        `${process.env.VUE_APP_API_GLOBAL_URL}api/v2/calculation-log/export`
      )
      url.searchParams.append(
        'from_date',
        this.chartDates.from_date.toISOString()
      )
      url.searchParams.append('to_date', this.chartDates.to_date.toISOString())

      for (const companyId of this.company_ids) {
        url.searchParams.append('company_ids[]', companyId)
      }

      return url.toString()
    },

    minFromDate() {
      let min = new Date(this.chartDates.to_date.getTime())
      min.setFullYear(min.getFullYear() - 2)

      // Limit the min date to november 2023, since we weren't tracking data from before then.
      return new Date(Math.max(min.getTime(), 1700398663787))
    },

    maxFromDate() {
      let max = new Date(this.chartDates.to_date.getTime())
      max.setMonth(max.getMonth())

      return max
    },

    minToDate() {
      let min = new Date()
      min.setFullYear(min.getFullYear() - 2)
      let fromDate = new Date(this.chartDates.from_date.getTime())
      fromDate.setMonth(fromDate.getMonth())

      return new Date(Math.max(min.getTime(), fromDate.getTime()))
    },

    maxToDate() {
      return new Date()
    },
  },

  watch: {
    chartDates: {
      handler(chartDates) {
        if (chartDates.from_date && chartDates.to_date) {
          this.fetchData()
        }
      },
      deep: true,
    },

    company_ids() {
      this.fetchData()
    },
  },

  mounted() {
    this.fetchData()
    this.fetchCompanies()
  },

  methods: {
    getDefaultStartDate() {
      const date = new Date()
      date.setMonth(date.getMonth() - 1)

      return date
    },

    getDefaultEndDate() {
      return new Date()
    },

    mapDates(callback) {
      const fromDate = new Date(this.chartDates.from_date.getTime())
      const toDate = new Date(this.chartDates.to_date.getTime())
      const mapped = []

      for (
        let date = new Date(fromDate);
        date <= toDate;
        date.setMonth(date.getMonth() + 1)
      ) {
        mapped.push(callback(date))
      }

      return mapped
    },

    async fetchCompanies() {
      const response = await api.get('/company', { _global: true })
      this.companies = response.data
    },

    fetchData() {
      api
        .post(
          `/calculation-log/chart/`,
          { ...this.chartDates, company_ids: this.company_ids },
          {
            _global: true,
          }
        )
        .then((response) => {
          for (const data of response.data) {
            this.allEnvironments.push(data.environment.name)
          }

          this.chartData.labels = this.generateDateLabels()
          const colorIterator = colorGenerator()
          this.chartData.datasets = []

          for (const environmentData of response.data) {
            const color = colorIterator.next().value
            const dataset = this.generateDatasetForEnvironment(
              environmentData,
              color
            )

            this.chartData.datasets.push(dataset)
          }
        })
        .finally(() => this.renderChart())
    },

    formatDate(date) {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        '0'
      )}`
    },

    generateDateLabels() {
      return this.mapDates(this.formatDate)
    },

    generateDatasetForEnvironment(environmentData, color) {
      return {
        label: environmentData.environment.name,
        backgroundColor: [color],
        borderWidth: 1,
        data: this.mapDates((date) => {
          return environmentData.data[this.formatDate(date)]?.[0]?.amount ?? 0
        }),
      }
    },

    renderChart() {
      const ctx = document.getElementById(this.chartId)

      if (this.chart) {
        this.chart.destroy()
      }

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
            },
          },
        },
      })
    },
  },
}
</script>

<style scoped>
.datePickers {
  display: flex;
  flex-direction: row;
  flex: 0;
}

.monthPicker {
  width: 10rem;
}

.header {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.autocompleteWidth {
  width: 100%;
  max-width: 18rem;
}
</style>
